import { ICacheClearArgs } from '../models/cache-clear-args';
import { ICacheReadArgs } from '../models/cache-read-args';
import { ICachedValue } from '../models/cached-value';
import { oneMinuteInMilliseconds } from '../models/constants';
import { ISillyLogger } from '../models/silly-logger';
import { getGlobalMap, resetGlobalMap } from '../utility/global-map';
import { Cache, getHash } from './cache';

const memoryCacheKey = 'in-a-cache:memory';
let memoryCache = getGlobalMap<Record<string, ICachedValue>>(memoryCacheKey);

export class MemoryCache extends Cache {
  protected cacheName = 'Memory';
  private readonly defaultRefreshAfterMilliseconds?: number;

  constructor(args?: {
    logger?: ISillyLogger;
    defaultRefreshAfterMilliseconds?: number;
  }) {
    super(args?.logger);
    this.defaultRefreshAfterMilliseconds =
      args?.defaultRefreshAfterMilliseconds || oneMinuteInMilliseconds;
  }

  public async read<T>(args: ICacheReadArgs<T>): Promise<T> {
    if (process.env.DISABLE_MEMORY_CACHE === 'true') {
      return args.retrieve();
    }
    const hash = getHash(args.bucket, args.id);
    const cached = memoryCache[hash];
    return this.handleCachedRead(
      cached,
      args,
      this.defaultRefreshAfterMilliseconds,
    );
  }

  public async clear(args?: ICacheClearArgs): Promise<void> {
    if (!args) {
      memoryCache =
        resetGlobalMap<Record<string, ICachedValue>>(memoryCacheKey);
    } else {
      const hash = getHash(args.bucket, args.id);
      memoryCache[hash] = null;
    }
  }

  public async clearByBucket(bucketToClear: string): Promise<void> {
    this.getHashesByBucket(bucketToClear).forEach(
      bucket => (memoryCache[bucket] = null),
    );
  }

  protected saveToCache(cached: ICachedValue): void {
    memoryCache[cached.hash] = cached;
  }

  private getHashesByBucket<T>(bucket: string): string[] {
    return Object.keys(memoryCache).filter(hash =>
      hash.startsWith(bucket + ':'),
    );
  }
}
